/**
 * Dashboard Store for AccountsGenie
 * Manages dashboard statistics and activity following PRD Phase 2 specifications
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { API_ENDPOINTS } from '../services/api';

export interface DashboardStats {
  totalTransactions: number;
  cashBalance: number;
  pendingInvoices: number;
  monthlyRevenue: number;
  // Trends (percentage change from previous period)
  transactionsTrend: number;
  cashBalanceTrend: number;
  invoicesTrend: number;
  revenueTrend: number;
}

export interface ActivityItem {
  id: string;
  timestamp: Date | string | number;
  type: 'transaction' | 'report' | 'sync' | 'error' | 'connection';
  description: string;
  status: 'success' | 'pending' | 'error';
  details?: any;
}

export interface ConnectionStatus {
  status: 'connected' | 'not_connected' | 'pending';
  lastHeartbeat: Date | null;
  pcInfo: {
    hostname: string;
    os: string;
    arch: string;
  } | null;
  tallyConnected: boolean;
  tenantId: string | null;
}

export interface DashboardState {
  // State
  stats: DashboardStats | null;
  recentActivity: ActivityItem[];
  connectionStatus: ConnectionStatus | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | string | number | null;
  
  // Actions
  fetchStats: () => Promise<void>;
  fetchActivity: () => Promise<void>;
  fetchConnectionStatus: () => Promise<void>;
  addActivity: (activity: Omit<ActivityItem, 'id' | 'timestamp'>) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  refreshDashboard: () => Promise<void>;
}

// Dashboard API functions - Phase 2 Implementation
// Dashboard shows TC connection status and basic state management
const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    // Phase 2: Show default stats while awaiting Tally connection
    // Real data will populate once TC establishes connection to Tally
    return {
      totalTransactions: 0,
      cashBalance: 0,
      pendingInvoices: 0,
      monthlyRevenue: 0,
      transactionsTrend: 0,
      cashBalanceTrend: 0,
      invoicesTrend: 0,
      revenueTrend: 0
    };
  },
  
  getActivity: async (): Promise<ActivityItem[]> => {
    // Phase 2: Show connection-related activities
    const now = new Date();
    return [
      {
        id: 'deploy_status',
        timestamp: now,
        type: 'connection',
        description: 'AccountsGenie deployed - awaiting Tally connection',
        status: 'pending'
      }
    ];
  },
  
  getConnectionStatus: async (): Promise<ConnectionStatus> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}${API_ENDPOINTS.connection.status}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get connection status');
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        const statusData = data.data;
        return {
          status: statusData.status === 'connected' ? 'connected' : 
                  statusData.status === 'pending' ? 'pending' : 'not_connected',
          lastHeartbeat: statusData.last_heartbeat ? new Date(statusData.last_heartbeat) : null,
          pcInfo: statusData.pc_info || null,
          tallyConnected: statusData.tally_connected || false,
          tenantId: statusData.tenant_id || null
        };
      }
      
      return {
        status: 'not_connected',
        lastHeartbeat: null,
        pcInfo: null,
        tallyConnected: false,
        tenantId: null
      };
    } catch (error) {
      console.error('Connection status check failed:', error);
      return {
        status: 'not_connected',
        lastHeartbeat: null,
        pcInfo: null,
        tallyConnected: false,
        tenantId: null
      };
    }
  }
};

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      // Initial state
      stats: null,
      recentActivity: [],
      connectionStatus: null,
      isLoading: false,
      error: null,
      lastUpdated: null,

      // Actions
      fetchStats: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const stats = await dashboardApi.getStats();
          
          set({
            stats,
            lastUpdated: new Date(),
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch dashboard statistics',
          });
        }
      },

      fetchActivity: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const activity = await dashboardApi.getActivity();
          
          set({
            recentActivity: activity,
            lastUpdated: new Date(),
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch recent activity',
          });
        }
      },

      fetchConnectionStatus: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const connectionStatus = await dashboardApi.getConnectionStatus();
          
          // Add connection status activity
          const statusActivity: ActivityItem = {
            id: `connection_${Date.now()}`,
            timestamp: new Date(),
            type: 'connection',
            description: connectionStatus.status === 'connected' 
              ? `TC Connected to ${connectionStatus.pcInfo?.hostname || 'Unknown PC'}${connectionStatus.tallyConnected ? ' with Tally' : ''}`
              : connectionStatus.status === 'pending'
              ? 'Deployed - Awaiting Tally Connection'
              : 'TC Disconnected',
            status: connectionStatus.status === 'connected' ? 'success' : 
                   connectionStatus.status === 'pending' ? 'pending' : 'error'
          };
          
          set((state) => ({
            connectionStatus,
            recentActivity: [statusActivity, ...state.recentActivity.filter(item => item.type !== 'connection')].slice(0, 20),
            lastUpdated: new Date(),
            isLoading: false,
            error: null,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch connection status',
          });
        }
      },

      addActivity: (activityData) => {
        const newActivity: ActivityItem = {
          ...activityData,
          id: `activity_${Date.now()}`,
          timestamp: new Date(),
        };
        
        set((state) => ({
          recentActivity: [newActivity, ...state.recentActivity].slice(0, 20), // Keep only last 20 items
        }));
      },

      refreshDashboard: async () => {
        const { fetchStats, fetchActivity, fetchConnectionStatus } = get();
        
        // Fetch all dashboard data in parallel
        await Promise.all([
          fetchStats(),
          fetchActivity(),
          fetchConnectionStatus(),
        ]);
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'dashboard-storage',
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          const value = localStorage.getItem(name);
          if (!value) return null;
          
          try {
            const parsed = JSON.parse(value);
            
            // Robust timestamp handling for recentActivity
            const processedActivity = parsed.state?.recentActivity?.map((item: any) => {
              let processedTimestamp: Date | string | number;
              
              try {
                // Try to convert to Date object if it's a valid date string
                if (typeof item.timestamp === 'string' || typeof item.timestamp === 'number') {
                  const dateObj = new Date(item.timestamp);
                  if (!isNaN(dateObj.getTime())) {
                    processedTimestamp = dateObj;
                  } else {
                    // Keep original if conversion fails
                    processedTimestamp = item.timestamp;
                  }
                } else if (item.timestamp instanceof Date) {
                  processedTimestamp = item.timestamp;
                } else {
                  // Fallback to current date if timestamp is invalid
                  processedTimestamp = new Date();
                }
              } catch (error) {
                // Fallback to current date if any error occurs
                processedTimestamp = new Date();
              }
              
              return {
                ...item,
                timestamp: processedTimestamp
              };
            }) || [];
            
            // Robust lastUpdated handling
            let processedLastUpdated: Date | null = null;
            try {
              if (parsed.state?.lastUpdated) {
                const dateObj = new Date(parsed.state.lastUpdated);
                if (!isNaN(dateObj.getTime())) {
                  processedLastUpdated = dateObj;
                }
              }
            } catch (error) {
              processedLastUpdated = null;
            }
            
            return JSON.stringify({
              ...parsed,
              state: {
                ...parsed.state,
                lastUpdated: processedLastUpdated,
                recentActivity: processedActivity
              }
            });
          } catch (error) {
            return value;
          }
        },
        setItem: (name: string, value: string) => {
          try {
            const parsed = JSON.parse(value);
            
            // Convert Date objects to ISO strings for storage
            const processedState = {
              ...parsed,
              state: {
                ...parsed.state,
                lastUpdated: parsed.state?.lastUpdated instanceof Date 
                  ? parsed.state.lastUpdated.toISOString() 
                  : parsed.state?.lastUpdated,
                recentActivity: parsed.state?.recentActivity?.map((item: any) => ({
                  ...item,
                  timestamp: item.timestamp instanceof Date 
                    ? item.timestamp.toISOString() 
                    : item.timestamp
                })) || []
              }
            };
            
            localStorage.setItem(name, JSON.stringify(processedState));
          } catch (error) {
            localStorage.setItem(name, value);
          }
        },
        removeItem: (name: string) => {
          localStorage.removeItem(name);
        }
      })),
      partialize: (state) => ({
        stats: state.stats,
        recentActivity: state.recentActivity,
        connectionStatus: state.connectionStatus,
        lastUpdated: state.lastUpdated,
      }),
    }
  )
);